import { useCallback, useEffect, useRef } from 'react';

/**
 * Scrolls the ref'd element into its nearest scrollable ancestor when needed.
 * Uses native scrollIntoView with block/inline "nearest", which only scrolls
 * when the element is not already fully visible.
 *
 * Pass `enabled` to auto-scroll when it becomes true. Prefer calling
 * `scrollIntoViewIfNeeded` after layout settles (e.g. Collapse onEntered)
 * when the element's size changes as it becomes visible.
 */
export function useScrollIntoViewIfNeeded<T extends Element>(
  enabled = false,
  behavior: ScrollBehavior = 'auto',
) {
  const ref = useRef<T>(null);

  const scrollIntoViewIfNeeded = useCallback(() => {
    ref.current?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior,
    });
  }, [behavior]);

  useEffect(() => {
    if (!enabled) return;
    scrollIntoViewIfNeeded();
  }, [enabled, scrollIntoViewIfNeeded]);

  return [ref, scrollIntoViewIfNeeded] as const;
}
